import { useEffect, useRef } from 'react'
import { api, ApiError } from '../lib/api'
import { clearTrackedShipmentId, getTrackedShipmentId, setTrackedShipmentId } from '../lib/driverTrackingSession'
import { createTrackQueue, type TrackQueue } from '../lib/trackQueue'

const SEND_INTERVAL_MS = 30_000

// The server's answer that the trip is over (cancelled, confirmed by the manager, or completed by
// the deadline after the driver's request), was never this account's (NOT_TRIP_DRIVER: another
// account signed in on this browser), or is not started (TRIP_NOT_STARTED: its start lapsed without
// a fix). Retrying is pointless: this trip's session ends here.
export function isTripOverError(error: unknown): boolean {
  return error instanceof ApiError
    && (error.code === 'SHIPMENT_CANCELLED' || error.code === 'SHIPMENT_CLOSED'
      || error.code === 'NOT_TRIP_DRIVER' || error.code === 'TRIP_NOT_STARTED')
}

// nextRunningTrip is another trip of the driver in transit — consolidated cargo: the end of one
// trip must not stop the others, so the stream moves to one of them. null when none is left.
export async function nextRunningTrip(endedId: string): Promise<string | null> {
  try {
    const shipments = await api.listDriverShipments()
    return shipments.find((s) => s.id !== endedId && s.status === 'in_progress')?.id ?? null
  } catch {
    return null
  }
}

/**
 * Keeps browser geolocation alive while the authenticated driver moves between
 * Mobile Web tabs. The selected shipment id is the same persisted value used by
 * the existing desktop driver screen, so both representations resume one session.
 */
export function useDriverWebTracking(enabled: boolean) {
  const watchId = useRef<number | null>(null)
  const intervalId = useRef<number | null>(null)
  const shipmentId = useRef<string | null>(null)
  const lastPosition = useRef<GeolocationPosition | null>(null)
  const lastSendAt = useRef(0)
  const queue = useRef<TrackQueue>(createTrackQueue())
  const lastQueuedAt = useRef<number | null>(null)

  useEffect(() => {
    if (!enabled || !navigator.geolocation) return

    const stop = () => {
      if (watchId.current != null) navigator.geolocation.clearWatch(watchId.current)
      if (intervalId.current != null) window.clearInterval(intervalId.current)
      watchId.current = null
      intervalId.current = null
      shipmentId.current = null
      lastPosition.current = null
    }

    const flush = async (id: string) => {
      try {
        await queue.current.flush((points) => api.sendLocations({ shipment_id: id, points }))
      } catch (error) {
        if (isTripOverError(error) && shipmentId.current === id) {
          queue.current.clear()
          const next = await nextRunningTrip(id)
          if (next && shipmentId.current === id) {
            setTrackedShipmentId(next)
            start(next)
            return
          }
          clearTrackedShipmentId()
          stop()
        }
      }
    }

    const send = async (position: GeolocationPosition, force = false) => {
      const id = shipmentId.current
      if (!id || (!force && Date.now() - lastSendAt.current < SEND_INTERVAL_MS)) return
      lastSendAt.current = Date.now()
      if (lastQueuedAt.current !== position.timestamp) {
        queue.current.enqueue({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy || undefined,
          ts: position.timestamp,
        })
        lastQueuedAt.current = position.timestamp
      }
      await flush(id)
    }

    const start = (id: string) => {
      stop()
      shipmentId.current = id
      queue.current = createTrackQueue()
      lastQueuedAt.current = null
      watchId.current = navigator.geolocation.watchPosition(
        (position) => { lastPosition.current = position; void send(position) },
        () => {},
        { enableHighAccuracy: true, maximumAge: SEND_INTERVAL_MS, timeout: 60_000 },
      )
      intervalId.current = window.setInterval(() => {
        if (lastPosition.current) void send(lastPosition.current)
      }, SEND_INTERVAL_MS)
    }

    const stored = getTrackedShipmentId()
    if (stored) start(stored)

    const onStart = (event: Event) => {
      const id = (event as CustomEvent<{ shipmentId?: string }>).detail?.shipmentId
      if (id) start(id)
    }
    const onStop = () => { queue.current.clear(); stop() }
    const onOnline = () => {
      const id = shipmentId.current
      if (!id || queue.current.pending() === 0) return
      void flush(id)
    }

    window.addEventListener('lupa:start-driver-tracking', onStart)
    window.addEventListener('lupa:stop-driver-tracking', onStop)
    window.addEventListener('online', onOnline)
    return () => {
      stop()
      window.removeEventListener('lupa:start-driver-tracking', onStart)
      window.removeEventListener('lupa:stop-driver-tracking', onStop)
      window.removeEventListener('online', onOnline)
    }
  }, [enabled])
}
